import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get('sort') || 'popular';

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];

  const filtered = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter(c => c.category === slug);
    if (sortParam === 'latest') {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortParam === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => b.views - a.views);
    }
    return result;
  }, [slug, sortParam]);

  const switchSort = (sort: string) => {
    setSearchParams({ sort });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const switchCategory = (catSlug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: `51漫画网 - ${currentCategory.name}漫画`,
        description: `51漫画网${currentCategory.name}漫画分类，提供丰富的${currentCategory.name}类漫画资源，高清在线阅读，每日更新。`,
        url: `https://www.51manhua.com/category/${slug}`,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: currentCategory.name },
            ]} />

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 font-bold mt-4 mb-2">
              {currentCategory.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              共 {filtered.length} 部{slug === 'all' ? '' : currentCategory.name}漫画等你来发现
            </p>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 flex-wrap">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => switchCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex-shrink-0 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <button
                onClick={() => switchSort('popular')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortParam === 'popular'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className="ri-fire-line mr-1"></i>最受欢迎
              </button>
              <button
                onClick={() => switchSort('rating')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortParam === 'rating'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className="ri-star-line mr-1"></i>评分最高
              </button>
              <button
                onClick={() => switchSort('latest')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortParam === 'latest'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className="ri-time-line mr-1"></i>最近更新
              </button>
            </div>

            {/* Comics Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {filtered.map(comic => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}